import { useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { IRichContentDocumentEditorPlaceholder } from '../../../models';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import {
	DonationsSummaryPlaceholder,
	LineOfBusinessPlaceholder,
	MattersPlaceholder,
	MeetingCancelLinkPlaceholder,
	MeetingCommentsPlaceholder,
	MeetingCompanyNamePlaceholder,
	MeetingDatePlaceholder,
	MeetingDayPlaceholder,
	MeetingDisplayNamePlaceholder,
	MeetingEmailAddressPlaceholder,
	MeetingFirstNamePlaceholder,
	MeetingLastNamePlaceholder,
	MeetingLocationPlaceholder,
	MeetingPhoneNumberPlaceholder,
	MeetingRescheduleLinkPlaceholder,
	MeetingTimePlaceholder,
	MeetingTimeZonePlaceholder,
	MeetingVirtualLocation,
	PoliciesExpiringPlaceholder,
	PoliciesPlaceholder,
	PolicyCarrierPlaceholder,
	PolicyEffectiveDatePlaceholder,
	PolicyNumberPlaceholder,
	PolicyPremiumPlaceholder,
	PolicyRenewalDatePlaceholder,
	RenewalKeyFactPlaceholder,
} from '../../../models/Token';

export interface IAutomationStepTestEmail {
	content: Api.IRawRichTextContentState;
	newAttachments?: File[];
	savedAttachments?: Api.IFileAttachment[];
	sendAttachmentsAsLink?: boolean;
	subject: string;
	templateReference?: Api.ITemplateReference | null;
}

interface ISendAutomationStepTestEmailVariables {
	contactId: string;
	email: IAutomationStepTestEmail;
}

/**
 * Offered in the customer app, including an admin signed in as the customer, where the test is a normal send
 * from the signed-in user. Under `impersonationContext` (the admin app) `POST /email` routes to the admin
 * scheduling endpoint, which holds the message for the customer's approval instead of sending it.
 */
export function canSendAutomationStepTestEmail(
	isAdminView: boolean,
	impersonationContext?: Api.IImpersonationContext
): boolean {
	return !isAdminView && !impersonationContext?.account?.id;
}

/**
 * The test uses the campaign composer's "Send a Test" path: a one-recipient `POST /email`. It must reach only the
 * chosen employee and change no contact's history, so the draft leaves out the step's CC ("also notify") list,
 * follow-up, keep-in-touch and note options.
 */
export function createAutomationStepTestEmailDraft(email: IAutomationStepTestEmail): Api.IEmailMessageDraft {
	return {
		attachments: email.savedAttachments?.length ? [...email.savedAttachments] : undefined,
		content: email.content,
		options: {
			saveAsNote: false,
			scheduledSend: { criteria: Api.ScheduleCriteria.Immediately },
			sendEmailFrom: Api.SendEmailFrom.CurrentUser,
		},
		// Automation emails always carry the sender's default signature, so the test does too.
		signatureTemplateId: 'default',
		subject: email.subject,
		templateReference: email.templateReference ?? undefined,
	};
}

/**
 * Merge fields the server fills only from the record that triggered the automation (a meeting, a policy, a
 * renewal). A test has no such record, so the server leaves them as typed or blank. Contact and sender fields
 * are not listed: they fill in from the recipient's contact and the current user, as in a real run.
 */
const TriggerOnlyPlaceholders: readonly IRichContentDocumentEditorPlaceholder[] = [
	MeetingDatePlaceholder,
	MeetingDayPlaceholder,
	MeetingTimePlaceholder,
	MeetingTimeZonePlaceholder,
	MeetingLocationPlaceholder,
	MeetingVirtualLocation,
	MeetingRescheduleLinkPlaceholder,
	MeetingCancelLinkPlaceholder,
	MeetingFirstNamePlaceholder,
	MeetingLastNamePlaceholder,
	MeetingPhoneNumberPlaceholder,
	MeetingCompanyNamePlaceholder,
	MeetingEmailAddressPlaceholder,
	MeetingCommentsPlaceholder,
	MeetingDisplayNamePlaceholder,
	RenewalKeyFactPlaceholder,
	PoliciesPlaceholder,
	PoliciesExpiringPlaceholder,
	PolicyNumberPlaceholder,
	LineOfBusinessPlaceholder,
	PolicyCarrierPlaceholder,
	PolicyEffectiveDatePlaceholder,
	PolicyRenewalDatePlaceholder,
	PolicyPremiumPlaceholder,
	MattersPlaceholder,
	DonationsSummaryPlaceholder,
];

function usesPlaceholder(textLowerCase: string, placeholder: IRichContentDocumentEditorPlaceholder): boolean {
	if (textLowerCase.includes(`data-placeholder-value="${placeholder.value.toLowerCase()}"`)) {
		return true;
	}
	return [placeholder.symbol, ...(placeholder.additionalSymbols ?? [])].some(symbol =>
		textLowerCase.includes(symbol.toLowerCase())
	);
}

/** Returns the display names of trigger-only merge fields in the subject or body, in a stable order. */
export function getTriggerOnlyMergeFieldNames(
	content: Api.IRawRichTextContentState | undefined,
	subject: string | undefined
): string[] {
	const html = content ? (Api.getContentHtmlStringValueFromRawRichTextContentState(content) ?? '') : '';
	const textLowerCase = `${subject ?? ''}\n${html}`.toLowerCase();
	const names = TriggerOnlyPlaceholders.filter(x => usesPlaceholder(textLowerCase, x)).map(x => x.text.toLowerCase());
	return [...new Set(names)];
}

export function getAutomationStepTestEmailNotice(
	content: Api.IRawRichTextContentState | undefined,
	subject: string | undefined
): string {
	const contactNotice = "Contact merge fields fill in from the recipient's own contact record.";
	const triggerOnlyNames = getTriggerOnlyMergeFieldNames(content, subject);
	if (!triggerOnlyNames.length) {
		return contactNotice;
	}
	return `${contactNotice} These fields fill in only when the automation runs, so the test leaves them blank or as typed: ${triggerOnlyNames.join(', ')}.`;
}

export function useSendAutomationStepTestEmail(impersonationContext?: Api.IImpersonationContext) {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('AutomationStepTestEmail');

	const mutation = useMutation({
		mutationFn: async ({ contactId, email }: ISendAutomationStepTestEmailVariables) => {
			const message = new Api.EmailMessageViewModel<File>(
				userSession,
				false,
				createAutomationStepTestEmailDraft(email)
			).impersonate(impersonationContext);
			message.sendAttachmentsAsLink = !!email.sendAttachmentsAsLink;
			if (email.newAttachments?.length) {
				message.setAttachments(new Api.AttachmentsToBeUploadedViewModel<File>([...email.newAttachments]));
			}
			message.contactsToAdd.add(
				new Api.ContactViewModel(userSession, { id: contactId }).impersonate(impersonationContext)
			);
			return message.send();
		},
		// TestEmailModal shows the failure to the user; this only records it.
		onError: error => logApiError('SendAutomationStepTestEmail-Error', Api.asApiError(error)),
	});

	return mutation.mutateAsync;
}
